import React, { useState } from "react";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Fade,
  Alert,
  Paper,
  IconButton,
} from "@mui/material";
import { Upload, Globe, Database, MessageSquare, Trash2, FileText } from "lucide-react";
import { LoadingSpinner } from "./LoadingSpinner";
import axios from "axios";
import { endpoint } from "../../resource/Constant";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";

type SetupStep = "url" | "crawling" | "pdf" | "vectorizing" | "complete";

interface SetupFlowProps {
  onComplete: () => void;
}

interface PdfFile {
  id: string;
  name: string;
  size: string;
  uploadedAt: Date;
  file: File;
}

export const SetupFlow = ({ onComplete }: SetupFlowProps) => {
  const [step, setStep] = useState<SetupStep>("url");
  const [url, setUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [pdfFiles, setPdfFiles] = useState<PdfFile[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const navigate = useNavigate()

  const { enqueueSnackbar } = useSnackbar();

  const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
      enqueueSnackbar(message, { variant: severity });
  };

  const handleUrlSubmit = async () => {
    if (!url) {
      showSnackbar("Please enter a website URL to crawl", "error");
      return;
    }
  let interval
  try {
    // Step 1: Set loader state while waiting for response
    setStep("crawling");
    setProgress(0);

    // Start the visual loader (simulate crawling progress)
    let currentProgress = 0;
    interval = setInterval(() => {
      currentProgress += Math.round(Math.random() * 5);
      if (currentProgress >= 90) currentProgress = 90; // cap at 90% until response
      setProgress(Math.min(currentProgress, 100));
    }, 500);
    // Step 2: Wait for API call


    try {
      const response = await axios.post(`${endpoint}/api/crawling/website-crawl`, 
        {website_link: url},
        {headers : {
          Authorization : `Bearer ${localStorage.getItem('crawling')}`
        }}
      )
      
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.detail || "Internal Server Error", "error")
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem('crawling')
        navigate('/crawling/signin')
        return;
      }
    }

    // Step 3: Once request succeeds, complete loader
    clearInterval(interval);
    setProgress(100);

    showSnackbar("Website Crawled Successfully", "success");

    // After a short delay, move to next step
    setTimeout(() => {
      setProgress(0);
      setStep("pdf");
    }, 800);

  } catch (error: any) {
    if (interval) clearInterval(interval); // ✅ always clear interval
    setProgress(0);
    setStep("url"); // go back to url input step
    showSnackbar(error?.response?.data?.detail?.[0]?.msg || error.message || "Error in Crawling Website", "error");
  }
};  


  const handlePdfSkipOrUpload = async (uploaded: boolean) => {
    if (uploaded) {
      showSnackbar("Your document has been added to the knowledge base", "success");
    }
    let interval
    try {
      setStep("vectorizing");
      setProgress(0);

      // Start the visual loader (simulate crawling progress)
      let currentProgress = 0;
      interval = setInterval(() => {
        currentProgress += Math.round(Math.random() * 15);
        if (currentProgress >= 90) currentProgress = 90; // cap at 90% until response
        setProgress(Math.min(currentProgress, 100));
      }, 500);

      try {
        const formData = new FormData();
        
        // Add all PDF files to FormData if they exist
        if (pdfFiles.length > 0) {
          pdfFiles.forEach((pdfFile) => {
            // Include filename to avoid boundary/mime quirks
            formData.append('files', pdfFile.file, pdfFile.file.name);
          });
        } else {
          // Ensure multipart has at least one field so boundary parsing doesn't fail
          formData.append('noop', '1');
        }

        // Always send FormData (even empty) so backend receives multipart
        await axios.post(
          `${endpoint}/api/crawling/vector-database`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('crawling')}`,
            },
            // Ensure axios sets the boundary; strip any global Content-Type
            transformRequest: [(data, headers) => {
              delete headers['Content-Type'];
              return data;
            }],
          }
        );
      } catch (error: any) {
        showSnackbar(error?.response?.data?.message || error?.response?.data?.detail || "Internal Server Error", "error")
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem('crawling')
          navigate('/crawling/signin')
          return;
        }
      }


      clearInterval(interval)
      setProgress(100);

      showSnackbar("Vector Database Created Successfully", "success");

      setTimeout(() => {
      setProgress(0);
      setStep("complete");
      }, 800);

    } catch (error: any) {
      if (interval) clearInterval(interval); // ✅ always clear interval
      setProgress(0);
      setStep("pdf"); // go back to url input step
      showSnackbar(error?.response?.data?.message || error.message || "Error in creating vector database", "error");
    }
  };

  const handleUploadPdf = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx";
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const newFiles: PdfFile[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          // Check file size (10MB limit)
          const fileSizeMB = file.size / 1024 / 1024;
          if (fileSizeMB > 10) {
            showSnackbar(`${file.name} exceeds 10MB limit`, "error");
            continue;
          }
          const newPdf: PdfFile = {
            id: `${Date.now()}-${i}`,
            name: file.name,
            size: `${fileSizeMB.toFixed(1)} MB`,
            uploadedAt: new Date(),
            file: file,
          };
          newFiles.push(newPdf);
        }
        if (newFiles.length > 0) {
          setPdfFiles([...pdfFiles, ...newFiles]);
          showSnackbar(`${newFiles.length} file(s) added to the list.`, "success");
        }
      }
    };
    input.click();
  };

  const handleRemovePdf = (id: string) => {
    const removedFile = pdfFiles.find(pdf => pdf.id === id);
    setPdfFiles(pdfFiles.filter(pdf => pdf.id !== id));
    if (removedFile) {
      showSnackbar(`${removedFile.name} has been removed.`, "info");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background orbs */}
      <Box
        sx={{
          position: "absolute",
          top: 80,
          left: 80,
          width: 384,
          height: 384,
          bgcolor: "primary.main",
          opacity: 0.2,
          borderRadius: "50%",
          filter: "blur(60px)",
          animation: "pulse 3s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.2 },
            "50%": { opacity: 0.3 },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 80,
          right: 80,
          width: 384,
          height: 384,
          bgcolor: "success.main",
          opacity: 0.2,
          borderRadius: "50%",
          filter: "blur(60px)",
          animation: "pulse 3s ease-in-out infinite",
          animationDelay: "1s",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.2 },
            "50%": { opacity: 0.3 },
          },
        }}
      />

      <Card
        sx={{
          width: "100%",
          maxWidth: 672,
          p: 4,
          position: "relative",
          zIndex: 1,
          boxShadow: 8,
          border: "2px solid",
          borderColor: "primary.main",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
        }}
      >
        <CardContent>
          {/* URL Input Step */}
          {step === "url" && (
            <Fade in timeout={500}>
              <Stack spacing={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1976d2, #66bb6a)",
                      mb: 2,
                      boxShadow: 6,
                      animation: "float 3s ease-in-out infinite",
                      "@keyframes float": {
                        "0%, 100%": { transform: "translateY(0px)" },
                        "50%": { transform: "translateY(-10px)" },
                      },
                    }}
                  >
                    <Globe size={40} color="white" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1, background: "linear-gradient(135deg, #1976d2, #66bb6a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    Let's Get Started
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Enter the website URL you'd like to create a chatbot for
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <TextField
                    type="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleUrlSubmit()}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 48,
                        fontSize: "1.125rem",
                      },
                    }}
                  />
                  <Button
                    onClick={handleUrlSubmit}
                    variant="contained"
                    fullWidth
                    sx={{
                      height: 48,
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      background: "linear-gradient(90deg, #1976d2, #42a5f5, #66bb6a)",
                      "&:hover": {
                        background: "linear-gradient(90deg, #1565c0, #1976d2, #42a5f5)",
                        transform: "scale(1.02)",
                        boxShadow: 8,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Start Crawling
                  </Button>
                </Stack>
              </Stack>
            </Fade>
          )}

          {/* Crawling Step */}
          {step === "crawling" && (
            <Fade in timeout={500}>
              <Box>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1976d2, #66bb6a)",
                      mb: 2,
                      boxShadow: 6,
                      animation: "pulse 2s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.7 },
                      },
                    }}
                  >
                    <Globe size={40} color="white" />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                    Crawling Website
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Discovering and analyzing all pages on <Typography component="span" sx={{ fontWeight: 500, color: "text.primary" }}>{url}</Typography>
                  </Typography>
                </Box>
                <LoadingSpinner
                  message="This may take a few moments depending on the size of the website..."
                  progress={progress}
                />
              </Box>
            </Fade>
          )}

          {/* PDF Upload Step */}
          {step === "pdf" && (
            <Fade in timeout={500}>
              <Stack spacing={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #66bb6a, #81c784)",
                      mb: 2,
                      boxShadow: 6,
                      animation: "float 3s ease-in-out infinite",
                      "@keyframes float": {
                        "0%, 100%": { transform: "translateY(0px)" },
                        "50%": { transform: "translateY(-10px)" },
                      },
                    }}
                  >
                    <Upload size={40} color="white" />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                    Additional Resources
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Want to add PDFs or documents to enhance your chatbot's knowledge?
                  </Typography>
                </Box>

                <Paper
                  component={Button}
                  onClick={handleUploadPdf}
                  variant="outlined"
                  sx={{
                    p: 6,
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Upload size={48} style={{ marginBottom: 16, color: "rgba(0,0,0,0.54)" }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Drag and drop your PDF here, or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports PDF files up to 10MB
                  </Typography>
                </Paper>

                {/* Selected PDFs List */}
                {pdfFiles.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.secondary" }}>
                      Selected Files ({pdfFiles.length})
                    </Typography>
                    <Stack spacing={1}>
                      {pdfFiles.map((pdf) => (
                        <Paper
                          key={pdf.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                            <FileText size={18} color="#1976d2" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {pdf.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pdf.size}
                              </Typography>
                            </Box>
                          </Stack>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemovePdf(pdf.id)}
                            sx={{ ml: 1 }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={() => handlePdfSkipOrUpload(false)}
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Skip for Now
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handlePdfSkipOrUpload(true)}
                    fullWidth
                    sx={{
                      height: 48,
                      fontWeight: 600,
                      background: "linear-gradient(90deg, #66bb6a, #81c784)",
                      "&:hover": {
                        background: "linear-gradient(90deg, #4caf50, #66bb6a)",
                        transform: "scale(1.02)",
                        boxShadow: 8,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Upload PDF
                  </Button>
                </Stack>
              </Stack>
            </Fade>
          )}

          {/* Vectorizing Step */}
          {step === "vectorizing" && (
            <Fade in timeout={500}>
              <Box>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #66bb6a, #81c784)",
                      mb: 2,
                      boxShadow: 6,
                      animation: "pulse 2s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.7 },
                      },
                    }}
                  >
                    <Database size={40} color="white" />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                    Creating Knowledge Base
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Processing and organizing your data for intelligent responses
                  </Typography>
                </Box>
                <LoadingSpinner
                  message="Building vector embeddings for semantic search..."
                  progress={progress}
                />
              </Box>
            </Fade>
          )}

          {/* Complete Step */}
          {step === "complete" && (
            <Fade in timeout={500}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #1976d2, #42a5f5, #66bb6a)",
                    mb: 2,
                    boxShadow: 12,
                    animation: "float 3s ease-in-out infinite",
                    "@keyframes float": {
                      "0%, 100%": { transform: "translateY(0px)" },
                      "50%": { transform: "translateY(-10px)" },
                    },
                  }}
                >
                  <MessageSquare size={48} color="white" />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    background: "linear-gradient(135deg, #1976d2, #66bb6a)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  All Set!
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Your chatbot is ready to assist visitors
                </Typography>
                <Button
                  onClick={onComplete}
                  variant="contained"
                  size="large"
                  sx={{
                    height: 56,
                    px: 5,
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    background: "linear-gradient(90deg, #1976d2, #42a5f5, #66bb6a)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #1565c0, #1976d2, #42a5f5)",
                      transform: "scale(1.05)",
                      boxShadow: 12,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Open Chatbot
                </Button>
              </Box>
            </Fade>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
